import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { useSubsequentEffect } from '@carvana/vdp-react-hooks';
import { datadogRum } from '@datadog/browser-rum';
import { useContext, useEffect, useState } from 'react';
import {
  useExperimentTiles,
  useTiles
} from '~/components/SearchResults/ResultsSection/hooks';
import { LOW_RESULTS_THRESHOLD } from '~/constants';
import SearchSource from '~/constants/SearchSource';
import {
  useInventoryIsLoaded,
  useInventoryVehicles,
  useSearchRequestId,
  useSimilarVehicles
} from '~/contexts/InventoryContext';
import SearchEventsDataContext from '~/contexts/SearchEventsDataContext';
import useCheckExperimentBucket from '~/hooks/useCheckExperimentBucket';
import useGetMegaEventData from './useGetMegaEventData';
import { useGetVehiclesLoaded } from './useGetVehiclesLoaded';
import useLastSearchRequestId from './useLastSearchRequestId';

function useTrackResults() {
  const { trackEvent } = useContext(AnalyticsContext);
  const [shouldFire, setShouldFire] = useState(false);
  const searchRequestId = useSearchRequestId();
  const inventoryIsLoaded = useInventoryIsLoaded();
  const inventoryVehicles = useInventoryVehicles();
  const eventData = useGetMegaEventData();
  const { searchSource, keywordSuggestion } = useContext(
    SearchEventsDataContext
  );

  const { lastSearchRequestId, updateLastSearchRequestId } =
    useLastSearchRequestId();

  const { vehicles: similarVehicles, searchRequestId: svSearchRequestId } =
    useSimilarVehicles();

  useEffect(() => {
    if (lastSearchRequestId.current !== searchRequestId) {
      setShouldFire(true);
    }
  }, [searchRequestId, svSearchRequestId, setShouldFire]);

  const isInTileExperiment = useCheckExperimentBucket(
    'srch-tile-redesign-2023',
    'experiment'
  );
  const controlTiles = useTiles(!isInTileExperiment);
  const experimentTiles = useExperimentTiles(isInTileExperiment);
  const tiles = isInTileExperiment ? experimentTiles : controlTiles;
  const shouldHaveSimilarVehicles = tiles.length < LOW_RESULTS_THRESHOLD;

  const { loadedInventoryVehicles, loadedSimilarVehicles } =
    useGetVehiclesLoaded({
      latestInventoryVehicles: inventoryVehicles,
      latestSimilarVehicles: similarVehicles,
      shouldHaveSimilarVehicles
    });

  // TODO potentially move additional events into separate maybeTrackX functions to be called here.
  useSubsequentEffect(() => {
    const isStillLoading =
      !loadedInventoryVehicles.isLoaded ||
      (!loadedSimilarVehicles.isLoaded && shouldHaveSimilarVehicles) ||
      !inventoryIsLoaded;

    const isSuggestionSource =
      searchSource === SearchSource.TextSearch ||
      searchSource === SearchSource.RecentSearch ||
      searchSource === SearchSource.SavedSearch;

    if (shouldFire && !isStillLoading) {
      const vehicleData = {
        ...eventData,
        previousSearchRequestId: lastSearchRequestId.current,
        vehicles: loadedInventoryVehicles.vehicles
      };

      trackEvent('mega-srp-tile-info', vehicleData);
      datadogRum.addAction('mega-srp-tile-info', {
        ...eventData,
        previousSearchRequestId: lastSearchRequestId.current
      });
      updateLastSearchRequestId(searchRequestId);

      if (isSuggestionSource && !loadedInventoryVehicles.vehicles.length) {
        trackEvent('no-results-from-suggestions', {
          suggestionText: keywordSuggestion?.suggestionText,
          source: searchSource,
          searchRequestId
        });
      }

      if (
        shouldHaveSimilarVehicles &&
        lastSearchRequestId.current !== svSearchRequestId
      ) {
        const similarVehiclesData = {
          ...eventData,
          searchRequestId: svSearchRequestId,
          vehicles: loadedSimilarVehicles.vehicles
        };

        trackEvent('tile-info-similar-results', similarVehiclesData);
        datadogRum.addAction('tile-info-similar-results', similarVehiclesData);
        updateLastSearchRequestId(svSearchRequestId);
      }

      setShouldFire(false);
    }
  }, [
    eventData,
    inventoryIsLoaded,
    keywordSuggestion,
    lastSearchRequestId,
    loadedInventoryVehicles,
    loadedSimilarVehicles,
    searchRequestId,
    searchSource,
    setShouldFire,
    shouldFire,
    shouldHaveSimilarVehicles,
    svSearchRequestId,
    updateLastSearchRequestId,
    trackEvent
  ]);
}

export default useTrackResults;
